import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import {
  GetTestKits,
  DeleteTestKit,
} from "../../../services/InventoryService";
import CommonLoader from "../../common/CommonLoader";
import ViewTestKit from "./ViewTestKit";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";

// Named apart from maintenance/test-kits/TestKitsTable (the customer kit catalog).
const InventoryTestKitsTable = () => {
  const navigate = useNavigate();

  const columns = [
    "Kit",
    "SKU",
    "Components",
    "Can Build",
    "Limited By",
    "Total Cost",
    "Active",
    "Actions",
  ];
  const searchableHeaders = ["kitName", "sku", "description", "limitingComponents"];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [testKitsData, setTestKitsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTestKit, setSelectedTestKit] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);

  useEffect(() => {
    fetchTestKits();
  }, []);

  useEffect(() => {
    const filteredResults = testKitsData.filter((testKit) =>
      searchableHeaders.some((header) =>
        testKit[header]
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
    );

    setFilteredData(filteredResults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, testKitsData]);

  const fetchTestKits = async () => {
    try {
      setLoading(true);
      const response = await GetTestKits();
      setTestKitsData(response.results);
      setFilteredData(response.results);
    } catch (error) {
      console.error("Error fetching test kits:", error);
      NotificationManager.error("Error fetching test kits");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddTestKit = () => {
    navigate("/vlp/inventory-test-kits/add");
  };

  const handleEditClick = (testKit) => {
    navigate(`/vlp/inventory-test-kits/${testKit.id}`);
  };

  // The list already carries the components and build numbers.
  const handleViewClick = (testKit) => {
    setSelectedTestKit(testKit);
  };

  const handleBackClick = () => {
    setSelectedTestKit(null);
    setSearchTerm("");
  };

  const handleDeleteClick = (testKit) => {
    setSelectedItemToDelete(testKit);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      await DeleteTestKit(selectedItemToDelete.id);
      setIsDeleteConfirmationOpen(false);
      const updatedTestKits = testKitsData.filter(
        (item) => item.id !== selectedItemToDelete.id
      );
      setTestKitsData(updatedTestKits);
      setFilteredData(updatedTestKits);
      NotificationManager.success(
        `${selectedItemToDelete.kitName} deleted successfully`
      );
    } catch (error) {
      setIsDeleteConfirmationOpen(false);
      console.error("Error deleting test kit:", error);
      NotificationManager.error("Error deleting test kit");
    }
  };

  const renderActions = (testKit) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        size="small"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(testKit)}
      >
        <EditIcon fontSize="small" />
      </IconButton>

      <IconButton
        aria-label="view"
        size="small"
        style={{ color: "green" }}
        onClick={() => handleViewClick(testKit)}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
      <IconButton
        aria-label="delete"
        size="small"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(testKit)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </div>
  );

  return (
    <div className="vlp-component-container">
      <span className="title">Test Kits </span>
      <hr />
      {selectedTestKit ? (
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewTestKit testKit={selectedTestKit} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddTestKit}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((testKit, index) => (
                      <TableRow
                        key={testKit.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        <TableCell>{testKit.kitName}</TableCell>
                        <TableCell>{testKit.sku}</TableCell>
                        <TableCell>{(testKit.items || []).length}</TableCell>
                        <TableCell>
                          <strong>{testKit.canBuild}</strong>
                        </TableCell>
                        <TableCell>{testKit.limitingComponents}</TableCell>
                        <TableCell>
                          ${Number(testKit.totalCost || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>{testKit.isActive ? "Yes" : "No"}</TableCell>
                        <TableCell>{renderActions(testKit)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
        </>
      )}

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message={`Are you sure you want to delete ${selectedItemToDelete?.kitName}?`}
        color="red"
      />
    </div>
  );
};

export default InventoryTestKitsTable;
